import React, { useState, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import useApi from "../../../utils/useApi";

const AddNewForm = () => {
    const {uploadForm} = useApi();
    const [files, setFiles] = useState([]);
    const [comments, setComments] = useState('');
    const [fileName, setFileName] = useState('');
    const [errors, setErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(selectedFiles);
    };

    const resetForm = () => {
        setFiles([]);
        setComments('');
        setFileName('')
        setErrors([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateForm = () => {
        const newErrors = [];
        if (files.length === 0) {
            newErrors.push('Please select at least one file to upload.');
        }
        setErrors(newErrors);
        return newErrors.length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('document_comments', comments);
            formData.append('fileName', fileName);

            files.forEach((file) => {
                formData.append('documents[]', file);
            });

            await uploadForm(formData);
            resetForm();
        } catch (error) {
            console.error('Upload error:', error);
            setErrors(prev => [...prev, error.message || 'Failed to upload form. Please try again.']);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="page-container p-4 md:px-6 md:py-6 bg-gray-50 dark:bg-gray-900 min-h-screen relative z-10">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-row flex-wrap justify-between items-center mb-8">
                    <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Add New Worksheet
                    </h3>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                            Add New Worksheet
                        </h4>
                    </div>

                    <div className="p-6">
                        {successMessage && (
                            <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-md">
                                {successMessage}
                            </div>
                        )}

                        {errors.map((error, index) => (
                            <div
                                key={index}
                                className="mb-4 p-4 text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 rounded-md flex items-start justify-between"
                            >
                                <p>{error}</p>
                                <button
                                    type="button"
                                    onClick={() => setErrors(errors.filter((_, i) => i !== index))}
                                    className="text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Form Name
                                </label>
                                <input
                                    value={fileName}
                                    onChange={(e) => setFileName(e.target.value)}
                                    placeholder="Add form name"
                                    type="text"
                                    className="mt-1 p-3 block w-full rounded-md border border-gray-300 dark:border-gray-600
                                    dark:bg-gray-800 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500
                                    focus:ring-opacity-50 sm:text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Form Comments
                                </label>
                                <textarea
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                    placeholder="Add form comments..."
                                    rows={4}
                                    className="mt-1 p-3 block w-full rounded-md border border-gray-300 dark:border-gray-600
                                    dark:bg-gray-800 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500
                                    focus:ring-opacity-50 sm:text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Upload Form
                                </label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={handleFileChange}
                                    multiple
                                    className="block w-full text-sm text-gray-500 dark:text-gray-400
                                    file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0
                                    file:text-sm file:font-medium file:bg-blue-50 dark:file:bg-blue-900/30
                                    file:text-blue-700 dark:file:text-blue-400 hover:file:bg-blue-100
                                    dark:hover:file:bg-blue-900/40"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`
                                    inline-flex items-center px-4 py-2 border border-transparent
                                    text-sm font-medium rounded-md shadow-sm text-white
                                    ${isSubmitting ?
                                      'bg-blue-400 cursor-not-allowed animate-pulse' :
                                      'bg-blue-500 hover:bg-blue-600'
                                }
                                    transition-colors duration-200
                                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                    dark:focus:ring-offset-gray-800
                                `}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {isSubmitting ? 'Uploading...' : 'Upload Form'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddNewForm;
